import { autoinject, computedFrom, transient, useView } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import * as Enumerable from "linq";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { VetClientModel, VetRiskScoreModel, VetRuleRunClientModel, VettingType } from "../../../../../../../../models/vetting/vetting.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService } from "../../../../../../../../services/basics/notification-client.service";
import { VettingService } from "../../../../../../../../services/vetting/vetting.service";
import { ChildComponent } from "../../../../../child-component";
import { ManageVesselService } from "../../../../../../../../services/vessels/manage-vessel.service";
import { VesselDetailModel } from "../../../../../../../../models/vessels/vessel.models";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { UserClaimsService } from "../../../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../../../constants/authentication/claims";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../../../../configuration/rightshipqi-router.constants";

@autoinject
@transient()
@useView('views/pages/modules/auto-screening/templates/pre-arrival-risk-review/tabs/summary/pre-arrival-risk-review-summary.html')
export class PreArrivalRiskReviewSummary extends ChildComponent<VetClientModel> {

    isLoading: boolean = true;
    latestRun: VetRuleRunClientModel;
    highestRiskLevel: string;
    vetRiskScores: VetRiskScoreModel[] = [];
    distributionColElements: Element[] = [];
    horizontalBarTicks: number;
    maxCategoryCount: number;
    canLinkAutoScreeningWithFullVet: boolean;

    constructor(notificationService: NotificationClientService, i18N: I18N, private vettingService: VettingService,
        private dialogClientService: DialogClientService,
        private manageVesselService: ManageVesselService,
        private userClaimsService: UserClaimsService,
        private router: Router) {
        super(notificationService, i18N);
    }

    async attached() {
        this.canLinkAutoScreeningWithFullVet = this.userClaimsService.hasPermission(Claims.CanLinkAutoScreeningWithFullVet);
        await this.reload();
    }

    @computedFrom("distributionColElements", "distributionColElements.length", "distributionColElements[0].clientWidth")
    get distributionColElementBarWidth(): number {
        return this.distributionColElements[0] ? this.distributionColElements[0].clientWidth / 5 : 0;
    }

    public vetRiskScoresLegends: { name: string, class: string }[] = [

        { name: "Passed Rules", class: "passed" },
        { name: "Failed Rules - Low Risk", class: "low" },
        { name: "Failed Rules - Medium Risk", class: "medium" },
        { name: "Failed Rules - High Risk", class: "high" }
    ];

    openRiskAnalysisRulesDialog(vetRiskScore: VetRiskScoreModel) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RiskAnalysisRulesDialog,
            {
                showPassed: true,
                riskCategoryId: vetRiskScore.riskCategoryId,
                categoryName: vetRiskScore.name,
                vetId: this.item.vetId,
                vesselName: this.item.vesselName,
                vesselIdentifier: this.item.internationalMaritimeOrganizationNumber
            }, true, false, false, "right");
    }

    async reload() {
        this.isLoading = true;
        const runs = await this.vettingService.getVetRuleRunsClientModel(this.item.vetId);
        if (runs && runs.length > 0) {
            this.latestRun = Enumerable.from(runs).orderByDescending(x => x.date).firstOrDefault();
        }

        this.vetRiskScores = Enumerable.from(this.item.vetRiskScores).where(x => x.name != 'All' && x.name != 'Critical')
            .orderByDescending(x => x.countHighFail).thenByDescending(x => x.countMediumFail).thenByDescending(x => x.countLowFail)
            .thenByDescending(x => x.countLowPass + x.countMediumPass + x.countHighPass).toArray();

        this.highestRiskLevel = Enumerable.from(this.vetRiskScores).orderByDescending(x => x.riskLevelId).select(x => x.riskLevel).firstOrDefault();
        this.maxCategoryCount = Enumerable.from(this.vetRiskScores).select(x => x.countHighPass + x.countLowPass + x.countMediumPass +
            x.countHighFail + x.countLowFail + x.countMediumFail).orderByDescending(x => x).firstOrDefault();
        this.horizontalBarTicks = Math.ceil(this.maxCategoryCount / 5.0);
        this.isLoading = false;
    }

    requestAFullVet() {
        const linkFullVetitem = CloneHelper.clone(this.item);
        // set cloned autoscreen vetId to 0 so that we create a new one from autoscreen
        linkFullVetitem.vetId = 0;
        // init these two properties as false because they does not exist in autoscreens
        linkFullVetitem.vetForSTS = false;
        linkFullVetitem.charterTypeId = null;
        this.manageVesselService.showChooseDialog(
            {
                vessel: <VesselDetailModel>{
                    vetRequiresMoreInformationEmails: this.item.vetRequiresMoreInformationEmails,
                    documentOfComplianceEmail: this.item.documentOfComplianceEmail,
                    cargoTypeCategoryId: this.item.cargoTypeCategoryId,
                    vesselId: this.item.vesselId,
                    internationalMaritimeOrganizationNumber: this.item.internationalMaritimeOrganizationNumber,
                    name: this.item.vesselName
                },
                vettingType: VettingType.FullVet,
                parentVetId: this.item.vetId,
                vet: linkFullVetitem,
                linkFullVetCheck: true

            });
    }

    navigateToFullVet() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Vet, { id: this.item.fullVetId });
    }
}
